import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate, formatApiErrorDetail, formatCurrency } from "../lib/api";
import { ClipboardText, Plus, Trash, PaperPlaneTilt, Copy, Download, ArrowSquareOut, FileText, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATO_META = {
  bozza:     { label: "Bozza",     cls: "bg-gray-200 text-gray-800 border-gray-300" },
  inviato:   { label: "Inviato",   cls: "bg-[#0033A0] text-white border-[#0033A0]" },
  compilato: { label: "Compilato", cls: "bg-[#FF9800] text-white border-[#FF9800]" },
  firmato:   { label: "Firmato",   cls: "bg-[#008A3C] text-white border-[#008A3C]" },
};

export default function AllegatiA() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const [aRes, cRes] = await Promise.all([api.get("/allegati-a"), api.get("/clients")]);
      setItems(aRes.data || []);
      setClients(cRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function sendItem(a) {
    if (!window.confirm(`Inviare il modulo a ${a.cliente_email || "(email mancante)"}?`)) return;
    try {
      await api.post(`/allegati-a/${a.id}/send`);
      toast.success("Modulo inviato. Link generato.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio");
    }
  }

  async function copyLink(a) {
    const url = `${window.location.origin}/a/${a.public_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato");
    } catch {
      window.prompt("Copia il link:", url);
    }
  }

  async function del(a) {
    if (!window.confirm(`Eliminare "${a.titolo}"?`)) return;
    try {
      await api.delete(`/allegati-a/${a.id}`);
      toast.success("Modulo eliminato");
      await load();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  }

  async function createQuote(a) {
    if (a.created_quote_id) {
      navigate(`/preventivi/${a.created_quote_id}/modifica`);
      return;
    }
    if (!window.confirm("Creare un preventivo precompilato con il piano e gli add-on selezionati dal cliente?")) return;
    try {
      const { data } = await api.post(`/allegati-a/${a.id}/create-quote`);
      toast.success(`Preventivo ${data.numero} creato (€ ${data.totale.toFixed(2)})`);
      navigate(`/preventivi/${data.quote_id}/modifica`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore creazione preventivo");
    }
  }

  const stats = useMemo(() => ({
    totali: items.length,
    firmati: items.filter((i) => i.stato === "firmato").length,
    in_attesa: items.filter((i) => i.stato === "inviato" || i.stato === "compilato").length,
  }), [items]);

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">e-Log HACCP</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Allegato A</h1>
          <p className="text-sm text-[#64748B] mt-3">{stats.totali} moduli · {stats.firmati} firmati · {stats.in_attesa} in attesa</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="btn-new-allegato"
          className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start"
        >
          <Plus size={14} weight="bold" /> Nuovo Modulo
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#64748B] text-sm">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="swiss-card p-12 text-center">
          <ClipboardText size={40} weight="duotone" className="mx-auto text-[#64748B] mb-3" />
          <h3 className="font-display text-xl font-bold mb-2">Nessun modulo</h3>
          <p className="text-sm text-[#64748B] mb-4">Genera un Allegato A da inviare al cliente per la firma del piano e-Log HACCP.</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm inline-flex items-center gap-2">
            <Plus size={14} weight="bold" /> Crea il primo modulo
          </button>
        </div>
      ) : (
        <div className="swiss-card overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F5F7]">
                <tr className="text-left">
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Cliente</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Piano</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Totale</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Stato</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Inviato</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Firmato</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => {
                  const meta = STATO_META[a.stato] || STATO_META.bozza;
                  return (
                    <tr key={a.id} className="border-t border-[#E2E8F0] hover:bg-[#F4F5F7]" data-testid={`allegato-row-${a.id}`}>
                      <td className="py-3 px-4">
                        <div className="font-semibold flex items-center gap-2">
                          {a.cliente_ragione_sociale || "—"}
                          {a.promo && <span className="text-[8px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded-sm border bg-red-50 text-[#D32F2F] border-[#D32F2F]" data-testid={`promo-badge-${a.id}`}>Promo</span>}
                        </div>
                        <div className="text-[11px] text-[#64748B]">{a.cliente_email || "—"}</div>
                      </td>
                      <td className="py-3 px-4 text-xs text-[#64748B]">
                        {a.piano ? `${a.piano.toUpperCase()} · annuale` : <span className="italic">non compilato</span>}
                      </td>
                      <td className="py-3 px-4 font-mono font-bold text-sm">
                        {a.totals?.totale_attivazione ? formatCurrency(a.totals.totale_attivazione) : "—"}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${meta.cls}`}>{meta.label}</span>
                      </td>
                      <td className="py-3 px-4 text-xs text-[#64748B]">{a.sent_at ? formatDate(a.sent_at) : "—"}</td>
                      <td className="py-3 px-4 text-xs text-[#64748B]">{a.signed_at ? formatDate(a.signed_at) : "—"}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex gap-1">
                          {a.stato === "bozza" && (
                            <button onClick={() => sendItem(a)} className="p-1.5 text-[#0033A0] hover:bg-blue-50 rounded-sm" title="Invia al cliente" data-testid={`send-${a.id}`}><PaperPlaneTilt size={16} weight="bold" /></button>
                          )}
                          <button onClick={() => copyLink(a)} className="p-1.5 text-[#0A0A0A] hover:bg-gray-100 rounded-sm" title="Copia link" data-testid={`copy-${a.id}`}><Copy size={16} /></button>
                          <a href={`/a/${a.public_token}`} target="_blank" rel="noreferrer" className="p-1.5 text-[#64748B] hover:text-[#0A0A0A] inline-flex items-center" title="Apri vista pubblica"><ArrowSquareOut size={16} /></a>
                          {a.stato === "firmato" && a.signed_pdf_file_id && (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/allegati-a/${a.public_token}/pdf`} target="_blank" rel="noreferrer" className="p-1.5 text-[#008A3C] hover:bg-green-50 rounded-sm inline-flex items-center" title="Scarica Modulo A firmato" data-testid={`pdf-${a.id}`}><Download size={16} weight="bold" /></a>
                          )}
                          {a.stato === "firmato" && (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/allegati-a/${a.public_token}/contratto`} target="_blank" rel="noreferrer" className="p-1.5 text-[#0A0A0A] hover:bg-gray-100 rounded-sm inline-flex items-center" title="Scarica Contratto SaaS integrale" data-testid={`contratto-${a.id}`}><ClipboardText size={16} weight="bold" /></a>
                          )}
                          {a.stato === "firmato" && (
                            <button
                              onClick={() => createQuote(a)}
                              data-testid={`create-quote-${a.id}`}
                              className={`p-1.5 rounded-sm inline-flex items-center ${a.created_quote_id ? "text-[#64748B] hover:bg-gray-100" : "text-[#0033A0] hover:bg-blue-50"}`}
                              title={a.created_quote_id ? "Apri preventivo già creato" : "Crea preventivo dai dati firmati"}
                            >
                              <Receipt size={16} weight={a.created_quote_id ? "regular" : "bold"} />
                            </button>
                          )}
                          <button onClick={() => del(a)} className="p-1.5 text-[#64748B] hover:text-[#D32F2F]" title="Elimina"><Trash size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="md:hidden divide-y divide-[#E2E8F0]">
            {items.map((a) => {
              const meta = STATO_META[a.stato] || STATO_META.bozza;
              return (
                <div key={a.id} className="px-4 py-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm">{a.cliente_ragione_sociale}</div>
                      <div className="text-[11px] text-[#64748B]">{a.cliente_email}</div>
                    </div>
                    <span className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${meta.cls}`}>{meta.label}</span>
                  </div>
                  {a.totals?.totale_attivazione > 0 && (
                    <div className="text-sm font-mono font-bold">{formatCurrency(a.totals.totale_attivazione)}</div>
                  )}
                  <div className="flex gap-2 flex-wrap mt-2">
                    {a.stato === "bozza" && a.cliente_email && (
                      <button onClick={() => sendItem(a)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0033A0]">Invia</button>
                    )}
                    <button onClick={() => copyLink(a)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0A0A0A]">Copia Link</button>
                    {a.stato === "firmato" && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/allegati-a/${a.public_token}/pdf`} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#008A3C]">PDF</a>
                    )}
                    {a.stato === "firmato" && (
                      <button onClick={() => createQuote(a)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0033A0]">{a.created_quote_id ? "Apri Preventivo" : "Crea Preventivo"}</button>
                    )}
                    <button onClick={() => del(a)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#D32F2F] ml-auto">Elimina</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showCreate && (
        <CreateModal clients={clients} onClose={() => setShowCreate(false)} onCreated={async () => { setShowCreate(false); await load(); }} />
      )}
    </div>
  );
}

function CreateModal({ clients, onClose, onCreated }) {
  const [clienteId, setClienteId] = useState("");
  const [rs, setRs] = useState("");
  const [email, setEmail] = useState("");
  const [promoBase, setPromoBase] = useState("");
  const [promoPlus, setPromoPlus] = useState("");
  const [promoNota, setPromoNota] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function onSelectClient(id) {
    setClienteId(id);
    const c = clients.find((x) => x.id === id);
    if (c) {
      setRs(c.ragione_sociale || "");
      setEmail(c.email || "");
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!rs.trim()) return toast.error("Ragione sociale obbligatoria");
    setSaving(true);
    try {
      await api.post("/allegati-a", {
        cliente_id: clienteId || null,
        cliente_ragione_sociale: rs.trim(),
        cliente_email: email.trim() || null,
        titolo: `Allegato A — ${rs.trim()}`,
        promo_base: promoBase ? parseFloat(promoBase) : null,
        promo_plus: promoPlus ? parseFloat(promoPlus) : null,
        promo_nota: promoNota.trim() || null,
      });
      toast.success("Modulo creato. Ora puoi inviarlo al cliente.");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-xl w-full max-h-[90vh] overflow-y-auto rounded-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E2E8F0]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#0033A0] mb-1">e-Log HACCP</div>
          <h3 className="font-display text-2xl font-bold">Nuovo Allegato A</h3>
          <p className="text-xs text-[#64748B] mt-1">Genera il link da inviare al cliente per compilare e firmare il modulo di scelta piano.</p>
        </div>
        <form onSubmit={submit} className="p-4 sm:p-6 space-y-4">
          <Field label="Cliente esistente">
            <select value={clienteId} onChange={(e) => onSelectClient(e.target.value)} className="inp" data-testid="ca-cliente-select">
              <option value="">— Cliente nuovo / non in elenco —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.ragione_sociale}{c.email ? ` · ${c.email}` : ""}</option>
              ))}
            </select>
          </Field>
          <Field label="Ragione sociale *">
            <input required value={rs} onChange={(e) => setRs(e.target.value)} placeholder="MIDNIGHT CAFE SRL" className="inp" data-testid="ca-rs-input" />
          </Field>
          <Field label="Email destinatario (opzionale, per invio automatico)">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="info@cliente.it" className="inp" data-testid="ca-email-input" />
            <span className="text-[10px] text-[#64748B] mt-1 block">Se vuoto, potrai comunque copiare il link e inviarlo come preferisci.</span>
          </Field>
          <PromoFields base={promoBase} setBase={setPromoBase} plus={promoPlus} setPlus={setPromoPlus} nota={promoNota} setNota={setPromoNota} />
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 border-t border-[#E2E8F0]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm">Annulla</button>
            <button type="submit" disabled={saving} data-testid="ca-save-btn" className="btn-primary px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center justify-center gap-2">
              <FileText size={14} weight="bold" /> {saving ? "Creazione…" : "Crea Modulo"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PromoFields({ base, setBase, plus, setPlus, nota, setNota }) {
  return (
    <div className="border border-dashed border-[#D32F2F]/40 bg-red-50/40 rounded-sm p-4 space-y-3" data-testid="promo-fields">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#D32F2F]">Prezzo promozionale (opzionale)</div>
        <div className="text-[11px] text-[#64748B] mt-1">Lascia vuoto per usare il listino. Il cliente vedrà il listino barrato; il prezzo promo resta bloccato anche ai rinnovi.</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Base · listino € 299">
          <input type="number" min="1" step="0.01" value={base} onChange={(e) => setBase(e.target.value)} placeholder="es. 199" className="inp" data-testid="ca-promo-base" />
        </Field>
        <Field label="Plus · listino € 499">
          <input type="number" min="1" step="0.01" value={plus} onChange={(e) => setPlus(e.target.value)} placeholder="es. 399" className="inp" data-testid="ca-promo-plus" />
        </Field>
      </div>
      <Field label="Nota promo (opzionale)">
        <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Promo lancio" maxLength={40} className="inp" data-testid="ca-promo-nota" />
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
